import { useId } from 'react';
import { Button } from '../../../components/Button';
import { TextInput } from '../../../components/TextInput';
import type { AdminTourFieldError, AdminTourStep } from '../../../types/adminTours';
import { TOUR_BODY_MAX_LENGTH, TOUR_TITLE_MAX_LENGTH } from '../../../types/adminTours';
import { hasEmDash, isOverridden } from './tourDraft';
import { parseStepFieldError } from './tourPresentation';

/**
 * Server field errors name the property they came from (`steps.calls.page.one.titleOverride`, or
 * `newSteps[0].title` for a step with no key yet). The name is parsed rather than searched, because
 * a step key can itself contain "title" or "body" and a substring match would put the message under
 * the wrong input. Anything that parses to neither is shown by the editor's summary instead of
 * being dropped.
 */
const errorFor = (errors: AdminTourFieldError[], part: 'title' | 'body'): string | undefined =>
  errors.find((error) => parseStepFieldError(error.field)?.part === part)?.message;

interface CopyCountProps {
  value: string;
  max: number;
  id: string;
  label: string;
}

const CopyCount = ({ value, max, id, label }: CopyCountProps) => {
  const over = value.length > max;
  return (
    <p className={`tour-step-form__count${over ? ' tour-step-form__count--over' : ''}`} id={id}>
      <span>
        {value.length}/{max} characters
      </span>
      {/* One region, always mounted: a live region that appears along with its text is missed by
          most screen readers. */}
      <span role="status" className="tour-step-form__warnings">
        {over && (
          <span className="tour-step-form__warning">
            {label} is {value.length - max} over the limit. The API will refuse it.
          </span>
        )}
        {hasEmDash(value) && (
          <span className="tour-step-form__warning" data-testid="tour-step-em-dash">
            {label} has an em dash. Swap it for a comma or a full stop so it reads like a person
            wrote it.
          </span>
        )}
      </span>
    </p>
  );
};

interface TourStepFormProps {
  step: AdminTourStep;
  errors: AdminTourFieldError[];
  onChange: (title: string, body: string) => void;
  onRevert: () => void;
  onClose: () => void;
}

/**
 * The copy for one step. Title and body only: everything structural about a step (its anchor, its
 * platform, its key) comes from the manifest and cannot be edited here.
 */
export const TourStepForm = ({ step, errors, onChange, onRevert, onClose }: TourStepFormProps) => {
  const fieldId = useId();
  const titleId = `${fieldId}-title`;
  const titleCountId = `${fieldId}-title-count`;
  const titleErrorId = `${titleId}-error`;
  const bodyId = `${fieldId}-body`;
  const bodyCountId = `${fieldId}-body-count`;
  const bodyErrorId = `${fieldId}-body-error`;
  const bodyError = errorFor(errors, 'body');
  const titleError = errorFor(errors, 'title');
  // The server's flag wins when it disagrees with the local text, so a revert is never hidden.
  // Code steps only: an admin's own step has no code default behind it to go back to, and offering
  // a revert there would blank the copy it was created with.
  const overridden = step.source === 'Code' && (step.hasOverride || isOverridden(step));

  return (
    <div className="tour-step-form" data-testid={`tour-step-form-${step.key}`}>
      <TextInput
        id={titleId}
        className="tour-step-form__field"
        label="Title"
        value={step.title}
        error={titleError}
        aria-describedby={titleError ? `${titleErrorId} ${titleCountId}` : titleCountId}
        onChange={(event) => onChange(event.target.value, step.body)}
        data-testid={`tour-step-title-${step.key}`}
      />
      <CopyCount
        value={step.title}
        max={TOUR_TITLE_MAX_LENGTH}
        id={titleCountId}
        label="The title"
      />

      <div className="tour-step-form__field">
        <label className="tour-step-form__label" htmlFor={bodyId}>
          Body
        </label>
        <textarea
          id={bodyId}
          className="tour-step-form__textarea"
          rows={4}
          value={step.body}
          aria-invalid={bodyError ? true : undefined}
          aria-describedby={bodyError ? `${bodyErrorId} ${bodyCountId}` : bodyCountId}
          onChange={(event) => onChange(step.title, event.target.value)}
          data-testid={`tour-step-body-${step.key}`}
        />
        {bodyError && (
          <span className="tour-step-form__error" id={bodyErrorId}>
            {bodyError}
          </span>
        )}
      </div>
      <CopyCount value={step.body} max={TOUR_BODY_MAX_LENGTH} id={bodyCountId} label="The body" />

      <div className="tour-step-form__actions">
        {overridden && (
          <Button
            type="button"
            variant="secondary"
            onClick={onRevert}
            data-testid={`tour-step-revert-${step.key}`}
          >
            Revert to default copy
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          data-testid={`tour-step-close-${step.key}`}
        >
          Done
        </Button>
      </div>
    </div>
  );
};
